<template>
  <v-autocomplete
    :loading="loading"
    v-show="userService.hasRight(rights) || ignoreRights"
    v-model="selection"
    :label="label"
    :disabled="disabled"
    :items="displayedUsers"
    :item-value="itemValue"
    @change="update"
    :rounded="rounded"
    :item-text="getItemText"
    :search-input.sync="search"
    menu-props="auto, overflowY"
    :filled="filled"
    :dark="dark"
    :outlined="outlined"
    :rules="[(v) => !!v || 'Auswahl ist erforderlich']"
    :filter="alwaysTrueFilter"
    noDataText="Kein Benutzer gefunden">
    <template v-slot:prepend-inner>
      <transition name="fade" mode="out-in">
        <v-progress-circular v-if="loading" key="progress" indeterminate color="primary" size="24" width="2"></v-progress-circular>
        <v-icon v-else key="icon">mdi-account-search-outline</v-icon>
      </transition>
    </template>

    <template v-slot:prepend-item>
      <div v-if="loading" class="px-1 pt-0">
        <v-skeleton-loader
          v-for="n in 4"
          :key="n"
          type="list-item"
          theme="dark"
          class="mb-2 fade-in"
          :style="{ 'animation-delay': `${n * 100}ms` }"></v-skeleton-loader>
      </div>
    </template>
  </v-autocomplete>
</template>
<script>
import api from "../api"
import userService from "../services/userService"
import abortService from "../services/abortService"

export default {
  name: "userSearch",
  props: {
    // Deine Props bleiben unverändert
    value: { default: null },
    noFetch: { type: Boolean, default: false },
    outlined: { type: Boolean, default: true },
    dark: { type: Boolean, default: false },
    hideEmail: { type: Boolean, default: false },
    filled: { type: Boolean, default: false },
    returnEmail: { type: Boolean, default: false },
    returnUser: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    label: { type: String, default: "Titel" },
    instructor: { type: Boolean, default: false },
    rights: { type: String, default: "" },
    ignoreRights: { type: Boolean, default: false },
    rounded: { type: Boolean, default: false },
  },
  data() {
    return {
      userService,
      abortService,
      users: [], // Hält NUR die Suchergebnisse
      initialUser: null, // Hält den per Prop übergebenen oder ausgewählten Benutzer
      selection: this.value ? this.value._id || this.value : null,
      search: null,
      loading: false,
      timerId: null,
      abortController: null,
    }
  },
  watch: {
    // Dieser Watcher ist entscheidend. Er reagiert, wenn die Eltern-Komponente
    // (der Anamnesebogen) den Wert von aussen ändert.
    value: {
      handler(newValue) {
        const newId = newValue ? newValue._id || newValue : null
        this.selection = newId
        // Wenn ein neuer Wert gesetzt wird, der nicht dem bereits geladenen
        // Benutzer entspricht, holen wir die Benutzerdaten, um den Namen anzuzeigen.
        if (newId && newId !== (this.initialUser?._id || null)) {
          this.fetchSingleUser(newId)
        }
        // Wenn der Wert entfernt wird, löschen wir auch den initialUser
        if (!newId) {
          this.initialUser = null
        }
      },
      immediate: true, // `immediate` sorgt dafür, dass der Watcher beim Erstellen der Komponente läuft
    },
    search(val) {
      // Startet die Suche nur, wenn wirklich getippt wird
      if (val && val.length >= 3) {
        // Starte die Suche erst ab 3 Zeichen
        this.fetchUsersDebounced(val)
      } else {
        // Wenn weniger als 3 Zeichen da sind, leere die Suchergebnisse
        this.users = []
      }
    },
  },
  created() {
    this.abortController = new abortService()
  },
  methods: {
    alwaysTrueFilter: () => true,

    fetchSingleUser(userId) {
      if (this.noFetch || !userId) return
      this.loading = true
      api
        .getUserById(userId)
        .then((response) => {
          // Wir speichern den Benutzer jetzt in 'initialUser', um ihn von
          // den Suchergebnissen getrennt zu halten.
          this.initialUser = response.user
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchUsersDebounced(val) {
      this.loading = true
      clearTimeout(this.timerId)
      this.timerId = setTimeout(() => this.fetchUsers(val), 500)
    },

    fetchUsers(val) {
      if (this.noFetch) return
      this.abortController.abort("not needed anymore")
      this.abortController = new abortService()
      if (localStorage.getItem("token")) {
        const apiCall = this.instructor ? api.lookupInstructors(val, this.abortController.signal) : api.lookupUsers(val, this.abortController.signal)

        apiCall
          .then((data) => {
            // Die API-Antwort überschreibt IMMER nur die Suchergebnis-Liste.
            this.users = data || []
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.loading = false
      }
    },

    update(selectedId) {
      // Wenn ein Benutzer aus der Dropdown-Liste ausgewählt wird,
      // setzen wir ihn sofort als 'initialUser'. Das verhindert das
      // Verschwinden des Namens beim Rausklicken.
      this.initialUser = this.displayedUsers.find((u) => u._id === selectedId)

      const emitValue = this.returnUser ? this.initialUser : selectedId
      this.$emit("update", emitValue)
    },

    getItemText(user) {
      if (!user) return ""
      if (this.hideEmail) return `${user.vorname || ""} ${user.nachname || ""}`.trim()
      return `${user.vorname || ""} ${user.nachname || ""} (${user.email || ""})`.trim()
    },
  },
  beforeDestroy() {
    if (this.noFetch) return
    this.abortController.abort("not needed anymore")
  },
  computed: {
    itemValue() {
      return this.returnEmail ? "email" : "_id"
    },
    displayedUsers() {
      // Diese Computed Property ist das Herzstück. Sie kombiniert den
      // initial/ausgewählten Benutzer mit den Suchergebnissen.
      if (this.initialUser) {
        const isInitialUserInSearchResults = this.users.some((user) => user._id === this.initialUser._id)
        if (!isInitialUserInSearchResults) {
          // Füge den initialen Benutzer am Anfang hinzu, wenn er nicht
          // schon in den Suchergebnissen vorhanden ist.
          return [this.initialUser, ...this.users]
        }
      }
      return this.users
    },
  },
}
</script>

<style lang="scss" scoped>
/* Transition für das Icon / den Ladekreis */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Staggered Animation für die Skeleton Loader */
.fade-in {
  opacity: 0; /* Startet unsichtbar, um ein Aufblitzen zu verhindern */
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px); /* Leichter Slide-Up Effekt */
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
